import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useServerFn } from '@tanstack/react-start'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshCw, Send, Loader2, Mail, AlertTriangle, CheckCircle2, Beaker, History, ChevronDown, ChevronRight } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import {
  listManualEmailOutbox,
  retryManualEmailOutbox,
  processManualEmailOutbox,
  listManualsForTestSend,
  testSendManualApproved,
  listManualEmailAudit,
  type OutboxItem,
  type OutboxStats,
  type TestSendManualOption,
  type ManualEmailAuditItem,
} from '@/lib/manual-email-outbox.functions'

export const Route = createFileRoute('/admin/manualy-emaily')({
  component: ManualyEmailyPage,
  head: () => ({
    meta: [
      { title: 'Emailová fronta manuálů — Admin' },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
})

const STATUS_LABEL: Record<string, string> = {
  queued: 'Ve frontě',
  sending: 'Odesílá se',
  sent: 'Odesláno',
  failed: 'Selhalo',
  dead: 'Vyčerpáno',
  suppressed: 'Blokováno',
}

function statusVariant(s: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'sent') return 'default'
  if (s === 'failed' || s === 'dead') return 'destructive'
  if (s === 'suppressed') return 'outline'
  return 'secondary'
}

const EVENT_LABEL: Record<string, string> = {
  created: 'Vytvořeno',
  backfill: 'Naimportováno',
  sending: 'Odesílá se',
  sent: 'Odesláno',
  retry: 'Retry',
  dead: 'Vyčerpáno',
  suppressed: 'Blokováno',
  status_change: 'Změna stavu',
  update: 'Změna',
}

function eventVariant(e: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (e === 'sent') return 'default'
  if (e === 'dead') return 'destructive'
  if (e === 'retry') return 'destructive'
  if (e === 'suppressed') return 'outline'
  return 'secondary'
}

function pickTitle(o: Record<string, string> | null | undefined) {
  if (!o) return '—'
  return o.cs || o.en || Object.values(o)[0] || '—'
}

function ManualyEmailyPage() {
  const { user, loading, hasRole } = useAuth()
  const isAdmin = hasRole('admin')
  const navigate = useNavigate()
  const list = useServerFn(listManualEmailOutbox)
  const retry = useServerFn(retryManualEmailOutbox)
  const process = useServerFn(processManualEmailOutbox)
  const listManuals = useServerFn(listManualsForTestSend)
  const testSend = useServerFn(testSendManualApproved)
  const listAudit = useServerFn(listManualEmailAudit)

  const [manuals, setManuals] = useState<TestSendManualOption[]>([])
  const [testEmail, setTestEmail] = useState('')
  const [testManualId, setTestManualId] = useState<string>('')
  const [testLang, setTestLang] = useState<string>('cs')
  const [testVersion, setTestVersion] = useState<string>('1')
  const [testBusy, setTestBusy] = useState(false)

  const [items, setItems] = useState<OutboxItem[]>([])
  const [stats, setStats] = useState<OutboxStats>({
    queued: 0, sending: 0, sent: 0, failed: 0, dead: 0, suppressed: 0, total: 0,
  })
  const [status, setStatus] = useState<string>('all')
  const [busy, setBusy] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  const [audit, setAudit] = useState<ManualEmailAuditItem[]>([])
  const [auditLoaded, setAuditLoaded] = useState(false)
  const [auditEvent, setAuditEvent] = useState<string>('all')
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const [rowAudit, setRowAudit] = useState<Record<string, ManualEmailAuditItem[]>>({})
  const [rowAuditBusy, setRowAuditBusy] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    const res = await list({ data: status === 'all' ? {} : { status } })
    setItems(res.items)
    setStats(res.stats)
    setLoaded(true)
  }, [list, status])

  const refreshAudit = useCallback(async () => {
    const rows = await listAudit({
      data: auditEvent === 'all' ? { limit: 200 } : { event: auditEvent, limit: 200 },
    })
    setAudit(rows)
    setAuditLoaded(true)
  }, [listAudit, auditEvent])

  const toggleRowAudit = useCallback(async (id: string) => {
    if (expandedRow === id) {
      setExpandedRow(null)
      return
    }
    setExpandedRow(id)
    if (!rowAudit[id]) {
      setRowAuditBusy(id)
      try {
        const rows = await listAudit({ data: { outbox_id: id, limit: 100 } })
        setRowAudit((prev) => ({ ...prev, [id]: rows }))
      } finally {
        setRowAuditBusy(null)
      }
    }
  }, [expandedRow, listAudit, rowAudit])

  useEffect(() => {
    if (!loading && !user) navigate({ to: '/auth', search: { redirect: '/admin/manualy-emaily' } })
  }, [loading, user, navigate])

  useEffect(() => { if (user && isAdmin) refresh() }, [user, isAdmin, refresh])
  useEffect(() => { if (user && isAdmin) refreshAudit() }, [user, isAdmin, refreshAudit])

  useEffect(() => {
    if (!user || !isAdmin) return
    listManuals({ data: undefined as any }).then((rows) => {
      setManuals(rows)
      if (rows.length && !testManualId) {
        setTestManualId(rows[0].id)
        setTestVersion(String(rows[0].latest_version))
        const langs = rows[0].available_langs
        if (langs.length) setTestLang(langs.includes('cs') ? 'cs' : langs[0])
      }
    }).catch(() => { /* ignore */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin])

  const selectedManual = useMemo(
    () => manuals.find((m) => m.id === testManualId) ?? null,
    [manuals, testManualId],
  )

  const failedCount = useMemo(() => stats.failed + stats.dead, [stats])

  const onRetry = async (id: string) => {
    setBusy(id)
    try {
      const r = await retry({ data: { id } })
      if (r.ok) toast.success('Odesláno')
      else toast.error(`Selhalo: ${r.error ?? 'neznámá chyba'}`)
      await refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Chyba')
    } finally {
      setBusy(null)
    }
  }

  const onProcess = async () => {
    setBusy('all')
    try {
      const r = await process({ data: {} })
      toast.success(`Zpracováno ${r.picked}: ${r.sent} odesláno, ${r.failed} selhalo`)
      await refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Chyba')
    } finally {
      setBusy(null)
    }
  }

  const onTestSend = async () => {
    if (!testEmail || !testManualId || !testLang || !testVersion) {
      toast.error('Vyplňte e-mail, manuál, jazyk i verzi.')
      return
    }
    const v = parseInt(testVersion, 10)
    if (!Number.isFinite(v) || v < 1) {
      toast.error('Verze musí být kladné celé číslo.')
      return
    }
    setTestBusy(true)
    try {
      const r = await testSend({
        data: { email: testEmail.trim(), manual_id: testManualId, lang: testLang, version: v },
      })
      if (r.ok) toast.success(`Testovací e-mail zařazen do fronty (${testEmail}).`)
      else toast.error(`Selhalo: ${r.error ?? 'neznámá chyba'}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Chyba')
    } finally {
      setTestBusy(false)
    }
  }

  if (loading || !user) return <div className="p-6">Načítání…</div>
  if (!isAdmin) return <div className="p-6 text-destructive">Přístup jen pro adminy.</div>

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
      <div className="flex items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Mail className="h-5 w-5" /> Emailová fronta manuálů
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={busy !== null}>
            <RefreshCw className="mr-1 h-4 w-4" /> Obnovit
          </Button>
          <Button size="sm" onClick={onProcess} disabled={busy !== null}>
            {busy === 'all' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
            Zpracovat frontu
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        {(['queued', 'sending', 'sent', 'failed', 'dead', 'suppressed'] as const).map((k) => (
          <Card key={k}>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">{STATUS_LABEL[k]}</div>
              <div className="text-xl font-semibold">{stats[k]}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {failedCount > 0 && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          {failedCount} chybných záznamů čeká na retry.
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Beaker className="h-4 w-4" /> Testovací odeslání „Schválení manuálu"
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="test-email">E-mail příjemce</Label>
            <Input
              id="test-email"
              type="email"
              placeholder="jmeno@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1">
            <Label>Manuál</Label>
            <Select
              value={testManualId}
              onValueChange={(v) => {
                setTestManualId(v)
                const m = manuals.find((x) => x.id === v)
                if (m) {
                  setTestVersion(String(m.latest_version))
                  if (m.available_langs.length && !m.available_langs.includes(testLang)) {
                    setTestLang(m.available_langs.includes('cs') ? 'cs' : m.available_langs[0])
                  }
                }
              }}
            >
              <SelectTrigger><SelectValue placeholder="Vyberte manuál" /></SelectTrigger>
              <SelectContent>
                {manuals.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {pickTitle(m.title_i18n)}{m.slug ? ` (${m.slug})` : ''}
                  </SelectItem>
                ))}
                {manuals.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">Žádné manuály</div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Jazyk</Label>
            <Select value={testLang} onValueChange={setTestLang}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(selectedManual?.available_langs?.length
                  ? selectedManual.available_langs
                  : ['cs', 'en', 'de', 'fr', 'es', 'it', 'pl', 'sk', 'pt', 'hr', 'ro', 'hu', 'bg']
                ).map((l) => (
                  <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="test-version">Verze</Label>
            <div className="flex gap-2">
              <Input
                id="test-version"
                type="number"
                min={1}
                step={1}
                value={testVersion}
                onChange={(e) => setTestVersion(e.target.value)}
              />
              <Button onClick={onTestSend} disabled={testBusy} className="whitespace-nowrap">
                {testBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
                Odeslat test
              </Button>
            </div>
            {selectedManual && (
              <div className="text-[11px] text-muted-foreground">
                Aktuální verze manuálu: v{selectedManual.latest_version}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">Záznamy</CardTitle>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Vše</SelectItem>
              <SelectItem value="queued">Ve frontě</SelectItem>
              <SelectItem value="sending">Odesílá se</SelectItem>
              <SelectItem value="sent">Odesláno</SelectItem>
              <SelectItem value="failed">Selhalo</SelectItem>
              <SelectItem value="dead">Vyčerpáno</SelectItem>
              <SelectItem value="suppressed">Blokováno</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="w-8 px-2 py-2"></th>
                <th className="px-3 py-2">Stav</th>
                <th className="px-3 py-2">Manuál</th>
                <th className="px-3 py-2">Verze</th>
                <th className="px-3 py-2">Příjemce</th>
                <th className="px-3 py-2">Pokusy</th>
                <th className="px-3 py-2">Chyba</th>
                <th className="px-3 py-2">Aktualizováno</th>
                <th className="px-3 py-2 text-right">Akce</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <Fragment key={it.id}>
                  <tr className="border-t">
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        aria-label={expandedRow === it.id ? 'Skrýt audit' : 'Zobrazit audit'}
                        onClick={() => toggleRowAudit(it.id)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {expandedRow === it.id
                          ? <ChevronDown className="h-4 w-4" />
                          : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={statusVariant(it.status)}>
                        {it.status === 'sent' ? <CheckCircle2 className="mr-1 inline h-3 w-3" /> : null}
                        {STATUS_LABEL[it.status] ?? it.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 max-w-[280px] truncate">{pickTitle(it.notification?.title_i18n ?? null)}</td>
                    <td className="px-3 py-2">v{it.notification?.version ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{it.email}</td>
                    <td className="px-3 py-2">{it.attempts}</td>
                    <td className="px-3 py-2 max-w-[240px] truncate text-xs text-muted-foreground" title={it.last_error ?? ''}>
                      {it.last_error ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(it.updated_at).toLocaleString('cs-CZ')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {it.status !== 'sent' && (
                        <Button size="sm" variant="outline" onClick={() => onRetry(it.id)} disabled={busy !== null}>
                          {busy === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                          <span className="ml-1">Odeslat znovu</span>
                        </Button>
                      )}
                    </td>
                  </tr>
                  {expandedRow === it.id && (
                    <tr className="border-t bg-muted/20">
                      <td colSpan={9} className="px-3 py-3">
                        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                          <History className="h-3.5 w-3.5" /> Auditní historie odesílání
                        </div>
                        {rowAuditBusy === it.id && (
                          <div className="text-xs text-muted-foreground">Načítání…</div>
                        )}
                        {rowAudit[it.id] && rowAudit[it.id].length === 0 && (
                          <div className="text-xs text-muted-foreground">Žádné události.</div>
                        )}
                        {rowAudit[it.id] && rowAudit[it.id].length > 0 && (
                          <table className="w-full text-xs">
                            <thead className="text-left text-[10px] uppercase text-muted-foreground">
                              <tr>
                                <th className="px-2 py-1">Kdy</th>
                                <th className="px-2 py-1">Událost</th>
                                <th className="px-2 py-1">Stav</th>
                                <th className="px-2 py-1">Pokus</th>
                                <th className="px-2 py-1">Důvod</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rowAudit[it.id].map((a) => (
                                <tr key={a.id} className="border-t border-border/40">
                                  <td className="px-2 py-1 text-muted-foreground">
                                    {new Date(a.created_at).toLocaleString('cs-CZ')}
                                  </td>
                                  <td className="px-2 py-1">
                                    <Badge variant={eventVariant(a.event)}>
                                      {EVENT_LABEL[a.event] ?? a.event}
                                    </Badge>
                                  </td>
                                  <td className="px-2 py-1">
                                    {a.from_status ? `${a.from_status} → ` : ''}{a.to_status ?? '—'}
                                  </td>
                                  <td className="px-2 py-1">{a.attempts ?? '—'}</td>
                                  <td className="px-2 py-1 text-muted-foreground">{a.last_error ?? '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {loaded && items.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Žádné záznamy.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" /> Auditní log e-mailů
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={auditEvent} onValueChange={setAuditEvent}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny události</SelectItem>
                <SelectItem value="created">Vytvořeno</SelectItem>
                <SelectItem value="sending">Odesílá se</SelectItem>
                <SelectItem value="sent">Odesláno</SelectItem>
                <SelectItem value="retry">Retry (selhalo)</SelectItem>
                <SelectItem value="dead">Vyčerpáno</SelectItem>
                <SelectItem value="suppressed">Blokováno</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={refreshAudit}>
              <RefreshCw className="mr-1 h-4 w-4" /> Obnovit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Kdy</th>
                <th className="px-3 py-2">Událost</th>
                <th className="px-3 py-2">Přechod</th>
                <th className="px-3 py-2">Pokus</th>
                <th className="px-3 py-2">Příjemce</th>
                <th className="px-3 py-2">Důvod</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-t">
                  <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(a.created_at).toLocaleString('cs-CZ')}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={eventVariant(a.event)}>{EVENT_LABEL[a.event] ?? a.event}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {a.from_status ? `${a.from_status} → ` : ''}{a.to_status ?? '—'}
                  </td>
                  <td className="px-3 py-2">{a.attempts ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{a.email ?? '—'}</td>
                  <td className="px-3 py-2 max-w-[320px] truncate text-xs text-muted-foreground" title={a.last_error ?? ''}>
                    {a.last_error ?? '—'}
                  </td>
                </tr>
              ))}
              {auditLoaded && audit.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Žádné auditní záznamy.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}
